import React from "react";

import {
  Button,
  Field,
  InfoDot,
  Input,
  Modal,
  Pill,
  StatusPill,
  Tooltip,
  useToast,
  type IconName,
} from "../../design";
import { askForRaise } from "../../lib/api";
import type { AuthHeaders } from "../../lib/auth";
import type { SpendBudget } from "../../lib/types";
import { toNumber } from "../../lib/format";
import { usd, usdShort } from "./util";

const HEALTH: Record<SpendBudget["state"], string> = {
  ok: "ok",
  at_risk: "at_risk",
  exceeded: "over",
};

const TONE: Record<SpendBudget["state"], string> = {
  ok: "var(--ds-ok-fill)",
  at_risk: "var(--ds-warn-fill)",
  exceeded: "var(--ds-bad-fill)",
};

// Policy, not state: neutral so it never competes with Health.
const ACTION: Record<
  SpendBudget["action"],
  { label: string; icon: IconName; hint: string }
> = {
  warn: {
    label: "Warn",
    icon: "mail",
    hint: "At the limit, admins get an email and requests keep going.",
  },
  block: {
    label: "Block",
    icon: "ban",
    hint: "At the limit, requests this budget covers are refused until the period resets or an admin raises it.",
  },
  approve: {
    label: "Needs approval",
    icon: "lock",
    hint: "At the limit, requests pause and the org's admins are asked to approve a raise.",
  },
};

const SPEND_INFO =
  "Spent so far this period out of the limit. Past the limit, a line on the bar marks it.";

const HEALTH_INFO =
  "On track: under 80% of the limit and not on pace to pass it. At risk: 80% used, or on pace to pass the limit before the period ends. Over: spending has reached the limit.";

/** Column headers for SpendCell and HealthPill, each with its explanation. */
export function SpendHead() {
  return (
    <span className="gov-th">
      Spend
      <InfoDot text={SPEND_INFO} />
    </span>
  );
}

export function HealthHead() {
  return (
    <span className="gov-th">
      Health
      <InfoDot text={HEALTH_INFO} />
    </span>
  );
}

export function HealthPill({ state }: { state: SpendBudget["state"] }) {
  return <StatusPill state={HEALTH[state]} />;
}

const OVER_NOW: Record<SpendBudget["action"], string> = {
  warn: "Requests keep running and admins were told",
  block: "Your requests are stopped until it resets or an admin raises it",
  approve: "Your requests are paused until an admin approves a raise",
};

/** The member's health pill; once over, hovering it says what that means
 * for their own requests. */
export function MemberHealth({ b }: { b: SpendBudget }) {
  if (b.state !== "exceeded") return <HealthPill state={b.state} />;
  return (
    <Tooltip text={OVER_NOW[b.action]} focusable>
      <Pill
        tone={b.action === "warn" ? "warn" : "bad"}
        icon={ACTION[b.action].icon}
      >
        {b.action === "block"
          ? "Requests blocked"
          : b.action === "approve"
            ? "Needs approval"
            : "Over limit"}
      </Pill>
    </Tooltip>
  );
}

export function ActionPill({ action }: { action: SpendBudget["action"] }) {
  const a = ACTION[action];
  return (
    <Tooltip text={a.hint} focusable>
      <Pill tone="outline" icon={a.icon}>
        {a.label}
      </Pill>
    </Tooltip>
  );
}

/** Spent of the limit and a bar that fills toward it (a line marks the
 * limit once passed). */
export function SpendCell({ b }: { b: SpendBudget }) {
  const spent = toNumber(b.spent_usd);
  const cap = toNumber(b.limit_usd);
  const scale = Math.max(cap, spent, 1e-9);
  const pctOf = (v: number) => `${Math.min(100, (v / scale) * 100)}%`;
  const over = spent - cap >= 0.005;
  return (
    <div
      className="gov-spend"
      style={{ "--gov-tone": TONE[b.state] } as React.CSSProperties}
    >
      <span className="gov-spendnow">
        <strong>{usdShort(spent)}</strong> of {usdShort(cap)}
      </span>
      <div
        className="gov-bar"
        role="meter"
        aria-valuemin={0}
        aria-valuenow={spent}
        aria-valuemax={cap}
        aria-label={`${usd(spent)} spent of a ${usd(cap)} limit`}
      >
        <span className="spent" style={{ width: pctOf(spent) }} />
        {over && (
          <span className="cap" style={{ left: `calc(${pctOf(cap)} - 1px)` }} />
        )}
      </div>
    </div>
  );
}

/** "Ask for more" on a budget that is at risk or over, then "Requested"
 * once the member has asked this period. */
export function RaiseAction({
  b,
  asked,
  onAsk,
}: {
  b: SpendBudget;
  asked: boolean;
  onAsk: () => void;
}) {
  if (asked)
    return (
      <Tooltip
        focusable
        text={`Your admins have your request. A raise they approve lasts until the ${b.period} ends.`}
      >
        <Pill tone="info" icon="clock">
          Requested
        </Pill>
      </Tooltip>
    );
  if (b.state === "ok") return null;
  return (
    <Button size="sm" onClick={onAsk}>
      Ask for more
    </Button>
  );
}

export function AskForMore({
  b,
  authHeaders,
  onClose,
  onSent,
}: {
  b: SpendBudget;
  authHeaders: AuthHeaders;
  onClose: () => void;
  onSent: () => void;
}) {
  const [note, setNote] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const toast = useToast();
  async function send() {
    setBusy(true);
    try {
      await askForRaise(authHeaders, b.id, note.trim() || null);
      toast("Request sent to your admins.");
      onSent();
    } catch {
      toast("Couldn't send the request. Nothing was sent; try again.", {
        tone: "bad",
      });
      setBusy(false);
    }
  }
  return (
    <Modal
      eyebrow={b.name}
      title="Ask for more budget"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={busy} onClick={() => void send()}>
            {busy ? "Sending…" : "Send request"}
          </Button>
        </>
      }
    >
      <form
        className="gov-ask"
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        <p className="gov-ask-lead">
          <strong>{usdShort(b.spent_usd)}</strong> of {usdShort(b.limit_usd)}{" "}
          spent this {b.period}. Your organization's admins see the request on
          their Budgets page, and a raise they approve lasts until the{" "}
          {b.period} ends.
        </p>
        <Field
          label="Why you need more"
          hint="Optional. Your admins see it with the request."
        >
          <Input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={500}
            placeholder="A migration this week runs the full test suite"
            block
          />
        </Field>
      </form>
    </Modal>
  );
}
